import "./index.css"
import { useDispatch, useSelector } from "react-redux";
import Header from "./components/header";
import Navigation from "./components/navigation";
import ManagerDetails from "./features/1_ManagerDetails";
import EmployeeDetails from "./features/2_EmployeeDetails";
import LicenseStates from "./features/3_LicenseStates";
import BranchLocationOption from "./features/4_5_6_BranchLocationDetails/5_locationOption";
import BranchLocationDetails from "./features/4_5_6_BranchLocationDetails/6_locationDetails";
import LockingCabinetDetails from "./features/7_LockingCabinet";
import LocalITCompanyDetails from "./features/8_localITCompanyDetails";
import OfficeSpaceOtherBusinessDetails from "./features/9_officeSpaceOtherBusinessDetails";
import MortgageSign from "./features/10_mortgageSign";
import ComplianceSignage from "./features/11_ComplianceSignage";
import OccupationalLicense from "./features/12_occupationalLicence";
import Summary from "./features/13_Summary";
import UtilitiesSelection from "./features/4_5_6_BranchLocationDetails/7_locationUtilities";
import MicroCutShredder from "./features/7_LockingCabinet/7.1_microCutShredder";
import MarginManagement from "./features/3.0_MarginManagement";
import ProcessingPlan from "./features/3.1_ProcessingPlan";
import OfficeSpaceOtherBusinessDetails2 from "./features/9_officeSpaceOtherBusinessDetails/9.1_otherBusinesses";
import { useEffect, useState } from "react";
import EmptyHeader from "./components/emptyHeader";
import { fetchEntrypoint, fetchLinkMeta, jumpToStep, resetNavigator, skipSteps } from "./features/FormNavigator/navigatorSlice";
import MarketingDetails from "./features/12.1_MarketingDetails";
import { fetchInitialState as fetchBranchLocationState } from "./features/4_5_6_BranchLocationDetails/branchLocationDetailsSlice";
import { calculateSkippedSteps, findNextUnskippedStep } from "./features/utils";
import Sidebar from "./admin/components/sidebar";

// The link-check API appends the admin's name to its messages
// ("… Please contact the Admin (Jane Doe)"). Show a clean message instead.
const LINK_ERROR_MESSAGES = [
  [/deleted/i, "This link has been deleted. Please contact your admin."],
  [/expired/i, "This link has expired. Please contact your admin for a new one."],
  [/only one submission/i, "This link has already been used. Only one submission is allowed per link."],
];

function friendlyLinkError(message) {
  if (!message) return "We could not open this link. Please refresh and try again.";
  const match = LINK_ERROR_MESSAGES.find(([pattern]) => pattern.test(message));
  return match ? match[1] : String(message).replace(/\s*\([^)]*\)\s*$/, "");
}

/**
 * App - Onboarding Shell Refactor
 */
function App() {
  const {currentStep, skippedSteps } = useSelector(state => state.navigator);
  const dispatch = useDispatch();
  // Read on the very first render — an effect would leave one render with no
  // token, which used to render as "Missing or invalid token."
  const [linkToken] = useState(() => new URLSearchParams(window.location.search).get('token'));
  const [isValid, setIsValid] = useState(false);
  const [error, setError] = useState("");
  const [isVerifying, setIsVerifying] = useState(Boolean(linkToken));

  useEffect(() => {
    if (linkToken) {
      dispatch(fetchEntrypoint(linkToken))
        .unwrap()
        .then(() => {
          setIsValid(true);
          setError("");
          setIsVerifying(false);
          // link answers given by the hiring manager (team type, formal DBA)
          dispatch(fetchLinkMeta());
          // The link is already verified at this point — a failed branch location
          // load only leaves the skipped steps as they are, it must not reset the
          // navigator and show a valid link as invalid.
          dispatch(fetchBranchLocationState())
            .unwrap()
            .then((branchLocationRes) => {
              const recalculatedSkippedSteps = calculateSkippedSteps(branchLocationRes?._option);
              dispatch(skipSteps(recalculatedSkippedSteps));
            })
            .catch((err) => console.error('Branch location load failed', err));
        }, (err) => {
          setIsValid(false);
          setIsVerifying(false);
          dispatch(resetNavigator());
          setError(friendlyLinkError(err?.error));
        });
    } else {
      setIsValid(false);
      dispatch(resetNavigator());
      setError("Missing or invalid token.");
    }
  }, [linkToken, dispatch]);

  useEffect(() => {
    if (skippedSteps.includes(currentStep)) {
      const adjustedStep = findNextUnskippedStep(currentStep, skippedSteps);
      dispatch(jumpToStep({ step: adjustedStep }));
    }
  }, [currentStep, skippedSteps, dispatch]);

  const renderStep = () => {
    const steps = {
      1: <ManagerDetails />,
      2: <EmployeeDetails />,
      3: <LicenseStates />,
      // 4: reserved (commented) — Branch Location Type, kept for future client use
      5: <ProcessingPlan />,
      6: <MarginManagement />,
      7: <BranchLocationOption />,
      8: <BranchLocationDetails />,
      9: <UtilitiesSelection />,
      10: <MicroCutShredder />,
      11: <LockingCabinetDetails />,
      12: <LocalITCompanyDetails />,
      13: <OfficeSpaceOtherBusinessDetails />,
      14: <OfficeSpaceOtherBusinessDetails2 />,
      15: <MortgageSign />,
      16: <ComplianceSignage />,
      17: <OccupationalLicense />,
      18: <MarketingDetails />,
      19: <Summary />
    };
    return steps[currentStep] || null;
  }

  // A link that is still being checked is not an error yet, so it does not get the
  // red treatment.
  const renderNoticeBox = (message) => (
    <div className="mx-auto my-10 max-w-xl rounded-xl border-l-4 border-slate-400 bg-white p-5 text-slate-700 shadow sm:p-6">
      <p className="text-base font-medium sm:text-lg">{message}</p>
    </div>
  );

  const renderErrorBox = (message) => (
    <div className="mx-auto my-10 max-w-xl rounded-xl border-l-4 border-red-500 bg-red-50 p-5 text-red-700 shadow-lg sm:p-6">
      <div className="flex">
        <svg className="mr-3 h-6 w-6 flex-shrink-0 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
        </svg>
        <p className="text-base font-medium sm:text-lg">{message}</p>
      </div>
    </div>
  );

  return (
    <div className="flex h-screen overflow-hidden bg-white font-sans">
      {/* Fixed Sidebar — public onboarding form: branding only, no nav options */}
      { isValid && <Sidebar active="" sideBarMenu={[]} handleSidebar={() => {}} /> }

      {/* Shell Container */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        
        {/* Fixed Header */}
        { isValid ? <Header /> : <EmptyHeader /> }
        
        {/* Scrollable Content */}
        <main className="relative flex-1 overflow-y-auto bg-[#f2ece9] pt-20">
          {/* pb leaves room below the navigation bar so the last field isn't hidden. */}
          <div className="mx-auto max-w-[1200px] px-3 pb-6 pt-4 sm:px-6 sm:pt-6 lg:px-8 lg:pt-8">
            {isValid ? (
              <>
                <div className="min-h-[50vh] rounded-2xl border border-slate-200 bg-white p-4 shadow-lg animate-in fade-in slide-in-from-bottom-4 duration-500 sm:p-6 lg:p-8">
                  {renderStep()}
                </div>
                {/* Sits right under the form; on a long step it sticks to the bottom
                    of the scroll area so Previous / Next stay reachable. */}
                <div className="sticky bottom-0 z-20 -mx-1 mt-4 bg-[#f2ece9] px-1 py-3 sm:py-4">
                  <Navigation />
                </div>
              </>
            ) : isVerifying ? (
              renderNoticeBox("Verifying your link...")
            ) : (
              renderErrorBox(error || "Missing or invalid token.")
            )}
          </div>
        </main>
      </div>
    </div>
  );
}

export default App;
